import { fetchData, postData } from 'modules/Axios';

export const requestTypes = { GET: 'GET', POST: 'POST' };
export const defaultState = {
  duplicateData: {
    currentName: '',
    suggestedDataKey: '',
    suggestedQueryKey: '',
    suggestedRequestType: 'POST',
    suggestedUrl: '',
    uniquenessDataKey: '',
    uniquenessQueryKey: '',
    uniquenessRequestType: 'POST',
    uniquenessUrl: '',
  },
  duplicateName: '',
  duplicateNameValid: true,
  isDuplicateNameUnique: true,
  isSuggestedNameLoading: false,
};

/**
 * Handler to Manage Appropriate Request Type - GET/POST - for Name Uniqueness Call
 * @function checkNameUniqueness
 * @return {Function} Dispatch
 */
export const checkNameUniqueness = () => {
  return async (dispatch, getState) => {
    const { duplicateData: { uniquenessRequestType } } = getState().duplicateModal;
    return uniquenessRequestType === requestTypes.POST
      ? dispatch(checkNameUniquenessPOST())
      : dispatch(checkNameUniquenessGET());
  };
};

export const CHECK_NAME_UNIQUENESS = 'CHECK_NAME_UNIQUENESS';
/**
 * Uses Provided Endpoint to `postData` if Given Name is Unique
 * @function checkNameUniquenessPOST
 * @return {Function} Dispatch of CHECK_NAME_UNQIUENESS Action Payload
 */
export const checkNameUniquenessPOST = () => {
  return async (dispatch, getState) => {
    const { duplicateName, duplicateData: { uniquenessDataKey, uniquenessUrl } } = getState().duplicateModal;

    const data = await dispatch(postData(uniquenessUrl, { name: duplicateName }))
      .catch(e => {
        console.log('There was an issue verifying name uniqueness...', e);
        return { [uniquenessDataKey]: false };
      });
    return dispatch({
      type: CHECK_NAME_UNIQUENESS,
      payload: data[uniquenessDataKey],
    });
  };
};

/**
 * Uses Provided Endpoint to `fetchData` if Given Name is Unique
 * @function checkNameUniquenessGET
 * @return {Function} Dispatch of CHECK_NAME_UNQIUENESS Action Payload
 */
export const checkNameUniquenessGET = () => {
  return async (dispatch, getState) => {
    const {
      duplicateName,
      duplicateData: { uniquenessDataKey, uniquenessQueryKey, uniquenessUrl },
    } = getState().duplicateModal;

    const data = await dispatch(fetchData(`${uniquenessUrl}?${uniquenessQueryKey}=${duplicateName}`))
      .catch(e => {
        console.log('There was an issue verifying name uniqueness...', e);
        return { [uniquenessDataKey]: false };
      });
    return dispatch({
      type: CHECK_NAME_UNIQUENESS,
      payload: data[uniquenessDataKey],
    });
  };
};

/**
 * Handler to Manage Appropriate Request Type - GET/POST - for Suggested name Call
 * @function fetchSuggestedName
 * @return {Function} Dispatch
 */
export const fetchSuggestedName = () => {
  return async (dispatch, getState) => {
    const { duplicateData: { suggestedRequestType } } = getState().duplicateModal;
    return suggestedRequestType === requestTypes.POST
      ? dispatch(fetchSuggestedNamePOST())
      : dispatch(fetchSuggestedNameGET());
  };
};

/**
 * Uses Provided Endpoint to `postData` to Get Suggested Name
 * @function fetchSuggestedNamePOST
 * @return {Function} Dispatch of checkNameUniqueness
 */
export const fetchSuggestedNamePOST = () => {
  return async (dispatch, getState) => {
    dispatch(suggestedNameLoading(true));
    const { currentName, suggestedDataKey, suggestedUrl } = getState().duplicateModal.duplicateData;

    const data = await dispatch(postData(suggestedUrl, { name: currentName }))
      .catch(e => {
        console.log('Issue fetching suggested duplicate name... ', e);
        return { [suggestedDataKey]: '' };
      });
    await dispatch(receiveSuggestedName(data[suggestedDataKey]));
    return dispatch(checkNameUniqueness());
  };
};

/**
 * Uses Provided Endpoint to `fetchData` to Get Suggested Name
 * @function fetchSuggestedNamePOST
 * @return {Function} Dispatch of checkNameUniqueness
 */
export const fetchSuggestedNameGET = () => {
  return async (dispatch, getState) => {
    dispatch(suggestedNameLoading(true));
    const {
      currentName,
      suggestedDataKey,
      suggestedDataQueryKey,
      suggestedUrl,
    } = getState().duplicateModal.duplicateData;

    const data = await dispatch(fetchData(`${suggestedUrl}?${suggestedDataQueryKey}=${currentName}`))
      .catch(e => {
        console.log('Issue fetching suggested duplicate name... ', e);
        return { [suggestedDataKey]: '' };
      });
    await dispatch(receiveSuggestedName(data[suggestedDataKey]));
    return dispatch(checkNameUniqueness());
  };
};

/**
 * Initializes the DuplicateModal -- Storing Data & Fetching Suggested Name
 * @param {Object} data
 * @return {Function} Dispatch of fetchSuggestedName()
 */
export const initializeDuplicateModal = data => {
  return async dispatch => {
    await dispatch(storeDuplicateModalData(data));
    return dispatch(fetchSuggestedName());
  };
};

export const RECEIVE_SUGGESTED_NAME = 'RECEIVE_SUGGESTED_NAME';
/**
 * Receives Suggested Name Payload from API
 * @function receiveSuggestedName
 * @param {string} suggestedName
 * @return {Object} RECEIVE_SUGGESTED_NAME Payload
 */
export const receiveSuggestedName = suggestedName => ({
  type: RECEIVE_SUGGESTED_NAME,
  payload: suggestedName,
});

export const RESET_DUPLICATE_MODAL = 'RESET_DUPLICATE_MODAL';
export const resetDuplicateModal = () => ({
  type: RESET_DUPLICATE_MODAL,
});

export const RESET_DUPLICATE_NAME = 'RESET_DUPLICATE_NAME';
/**
 * Resets Current Duplicate Name
 * @function resetDuplicateName
 * @return {Object} RESET_DUPLICATE_NAME Payload
 */
export const resetDuplicateName = () => ({
  type: RESET_DUPLICATE_NAME,
});

export const STORE_DUPLICATE_MODAL_DATA = 'STORE_DUPLICATE_MODAL_DATA';
/**
 * Stores Provided Props Necessary for API Calls
 * @param {Object} data
 * @return {Object} STORE_DUPLICATE_MODAL_DATA Payload
 */
export const storeDuplicateModalData = data => ({
  type: STORE_DUPLICATE_MODAL_DATA,
  payload: data,
});

export const SUGGESTED_NAME_LOADING = 'SUGGESTED_NAME_LOADING';
/**
 * Sets or resets the loading flag for suggested name fetching
 * @param {boolean} bool
 * @return {Object} SUGGESTED_NAME_LOADING Payload
 */
export const suggestedNameLoading = bool => ({
  type: SUGGESTED_NAME_LOADING,
  payload: bool,
});

export const UPDATE_DUPLICATE_NAME = 'UPDATE_DUPLICATE_NAME';
/**
 * Updates Current Duplicate Name
 * @function updateDuplicateName
 * @param {string} duplicateName
 * @return {Object} UPDATE_DUPLICATE_NAME Payload
 */
export const updateDuplicateName = (duplicateName, validationFn) => ({
  type: UPDATE_DUPLICATE_NAME,
  payload: {
    duplicateName,
    duplicateNameValid: !!validationFn(duplicateName),
  },
});

export default function reducer(state = defaultState, action) {
  switch (action.type) {
    case CHECK_NAME_UNIQUENESS:
      return { ...state, isDuplicateNameUnique: action.payload };
    case RECEIVE_SUGGESTED_NAME:
      return {
        ...state,
        duplicateName: action.payload,
        isSuggestedNameLoading: false,
      };
    case RESET_DUPLICATE_MODAL:
      return { ...defaultState };
    case RESET_DUPLICATE_NAME:
      return {
        ...state,
        duplicateName: '',
        duplicateNameValid: true,
        isDuplicateNameUnique: true,
        isSuggestedNameLoading: false,
      };
    case STORE_DUPLICATE_MODAL_DATA:
      return { ...state, duplicateData: action.payload };
    case SUGGESTED_NAME_LOADING:
      return { ...state, isSuggestedNameLoading: action.payload };
    case UPDATE_DUPLICATE_NAME:
      return {
        ...state,
        duplicateName: action.payload.duplicateName,
        duplicateNameValid: action.payload.duplicateNameValid,
      };
    default:
      return state;
  }
}
